import { useContext, useEffect, useMemo, useState } from "react";
import "../styles/searchresults.css";
import "../styles/categorysilder.css";
import { FaRegHeart, FaHeart } from "react-icons/fa";
import { useSearchParams, useNavigate } from "react-router-dom";
import noimage from "../assets/noimage.webp";
import { fetchSearchResultsAPI } from "../services/Tools";
import { CartContext } from "../contextApi/CartContext";
import {
  AddItemToWishList,
  AddItemToCart,
  DeleteFromWishList,  
  getProductQty,
} from "../services/Cart";
import { Notifyed } from "../services/Auth";
import { showSnackbar } from "../comopnents/snackbar";
import AuthModal from "../comopnents/AuthModal";
import LoadingSpinner from "../comopnents/LoadingSpinner";
import CartQuantityControl from "../comopnents/CartQuantityControl";
import PurchaseOptionsModal from "../comopnents/PurchaseOptionsModal";
import { useAuthGuard } from "../comopnents/useAuthGuard";
import {
  buildCartPurchaseData,
  buildEnhancedPurchaseData,
  getMinOrderQty,
  getMinimumPurchaseQuantity,
  getPurchaseQuantityStep,
  getSavedCartPurchaseData,
  shouldShowPurchaseModal,
} from "../utils/purchaseOptions";
import { isOutOfStock } from "../utils/stock";
import { FaStar } from "react-icons/fa";


function SearchResults() {
  const isLoggedIn = Boolean(localStorage.getItem("token"));
  const { requireAuth, authModal } = useAuthGuard();
  const { addToCart, itemList, setCartQty } = useContext(CartContext);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [wishlist, setWishlist] = useState({});
  const [wishlistItems, setWishlistItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedModels, setSelectedModels] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [maxPrice, setMaxPrice] = useState(0);
  const [priceLimit, setPriceLimit] = useState(0);
  const [filteredMaxPrice, setFilteredMaxPrice] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingCartId, setPendingCartId] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [pendingPurchaseProduct, setPendingPurchaseProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortOption, setSortOption] = useState("");
  const [filterOption, setFilterOption] = useState("");
  const [minDiscount, setMinDiscount] = useState(0)
  const [params] = useSearchParams();
  const query = params.get("query");
  const navigate = useNavigate();

  const goToProductDetail = (productId) => {
    navigate(`/productdetail?productid=${productId}`);
  };

  const getProductImage = (product) =>
    product?.image1?.trim() ||
    product?.image2?.trim() ||
    product?.image3?.trim() ||
    product?.image?.trim() ||
    product?.dtl?.[0]?.image1?.trim() ||
    noimage;

  const getProductPricing = (product) => {
    const mrp = Number(product?.mrp || product?.dtl?.[0]?.mrp || 0);
    const discount = Number(product?.discount || product?.dtl?.[0]?.discount || 0);

    if (!mrp) return null;

    return {
      mrp,
      netrate: Number(product?.netrate || product?.dtl?.[0]?.netrate || 0),
      finalPrice: discount ? Math.round(mrp - (mrp * discount) / 100) : mrp,
    };
  };

  // ----- Fetch Search Results -----
  const fetchSearchResults = async () => {
    try {
      setLoading(true);
      const data = await fetchSearchResultsAPI(query);

      // Remove duplicate products by productid
      const uniqueProducts = Array.from(
        new Map(data.map((item) => [item.productid, item])).values(),
      );

      setProducts(uniqueProducts);

      const highestPrice = Math.max(...uniqueProducts.map((p) => p.mrp || 0));
      setPriceLimit(highestPrice);
      setMaxPrice(highestPrice);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setLoading(false);
    }
  };

  const uniqueBrands = useMemo(() => {
    let result = [...products];

    if (selectedCategory) {
      result = result.filter((p) => p.category === selectedCategory);
    }

    if (selectedModels.length) {
      result = result.filter((p) =>
        selectedModels.includes(p.modelno || p.displaymodelno),
      );
    }

    return [...new Set(result.map((p) => p.brand).filter(Boolean))];
  }, [products, selectedCategory, selectedModels]);

  useEffect(() => {
    fetchSearchResults();
  }, [query, sortOption, filterOption]);

  // ----- Calculate Categories & Models -----
  const categories = useMemo(() => {
    const map = {};
    products.forEach((p) => {
      if (!map[p.category]) map[p.category] = 0;
      map[p.category]++;
    });
    return Object.keys(map).map((k) => ({ name: k, count: map[k] }));
  }, [products]);

  const models = useMemo(() => {
    const map = {};
    products.forEach((p) => {
      const model = p.modelno || p.displaymodelno;
      if (!map[model]) map[model] = 0;
      map[model]++;
    });
    return Object.keys(map).map((k) => ({ name: k, count: map[k] }));
  }, [products]);  

  // ----- Filter Products -----
  useEffect(() => {
    let result = [...products];

    if (selectedCategory) {
      result = result.filter((p) => p.category === selectedCategory);
    }

    if (selectedModels.length) {
      result = result.filter((p) =>
        selectedModels.includes(p.modelno || p.displaymodelno),
      );
    }
    if (minDiscount > 0) {
      result = result.filter((p) => (p.discount || 0) >= minDiscount);
    }
    // ✅ Brand filter (NEW)
    if (selectedBrands.length) {
      result = result.filter((p) => selectedBrands.includes(p.brand));
    }

    result = result.filter((p) => {
      let price = p.mrp;
      if (p.discount) price = price - (price * p.discount) / 100;
      return price <= maxPrice; 
    });

    setFilteredProducts(result);

    const filteredMax = result.length
      ? Math.max(
        ...result.map((p) => {
          let price = p.mrp;
          if (p.discount) price = price - (p.discount * price) / 100;
          return price;
        }),
      )
      : 0;
    setFilteredMaxPrice(filteredMax);
  }, [products, selectedCategory, selectedModels, maxPrice, selectedBrands, minDiscount]);

  // ----- Display Max Price -----
  const displayMaxPrice = useMemo(() => {
    if (!filteredProducts.length) return 0;
    return Math.max(
      ...filteredProducts.map((p) => {
        let price = p.mrp;
        if (p.discount) price = price - (p.discount * price) / 100;
        return price;
      }),
    );
  }, [filteredProducts]);


  const calculateFilteredPriceLimit = () => {
    let filteredProducts = [...products];
    if (selectedCategory) {
      filteredProducts = filteredProducts.filter(
        (p) => p.category === selectedCategory,
      );
    }
    if (selectedModels.length) {
      filteredProducts = filteredProducts.filter((p) =>
        selectedModels.includes(p.modelno || p.displaymodelno),
      );
    }
    if (!filteredProducts.length) return 0;
    return Math.max(
      ...filteredProducts.map((p) => {
        let price = p.mrp;
        if (p.discount) price = price - (p.discount * price) / 100;
        return price;
      }),
    );
  };

  useEffect(() => {
    const newPriceLimit = calculateFilteredPriceLimit();
    setPriceLimit(newPriceLimit);
    setMaxPrice(newPriceLimit);
  }, [products, selectedCategory, selectedModels]);

  const openPurchaseModal = (product) => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      setPendingCartId({ product });
      setShowAuthModal(true);
      return;
    }

    if (!shouldShowPurchaseModal(product)) {
      const purchaseData = buildCartPurchaseData(product, "piece");
      handleAddToCart(product, purchaseData.quantity, purchaseData);
      return;
    }
    setPendingPurchaseProduct(product);
    setShowPurchaseModal(true);
  };

  const confirmPurchaseType = async (purchaseType, couponData) => {
    if (!pendingPurchaseProduct) return;
    const purchaseData = buildEnhancedPurchaseData(pendingPurchaseProduct, purchaseType, couponData);
    await handleAddToCart(pendingPurchaseProduct, purchaseData.quantity, purchaseData);
    setShowPurchaseModal(false);
    setPendingPurchaseProduct(null);
  };

  // ----- Handle Cart -----
  const handleAddToCart = async (product, qty, purchaseData = null) => {
    const id = product?.productid || product;
    if (isOutOfStock(product)) {
      await handleNotifyMe(id);
      return;
    }

    if (!requireAuth(() => handleAddToCart(product, qty, purchaseData))) return;

    try {
      const userId = localStorage.getItem("userId");
      await AddItemToCart({ customerid: userId, productid: id, qty, stock: product?.stock });
      addToCart(id, qty, purchaseData);
      showSnackbar("success", "Item Added to Cart!");
    } catch (error) {
      console.error("Add to cart error:", error);
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    if (pendingCartId) {
      handleAddToCart(pendingCartId.product || pendingCartId.id, pendingCartId.qty);
      setPendingCartId(null);
    }
  };

  const handleCartQtyChange = async (productId, newQty) => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      setPendingCartId({ id: productId, qty: newQty });
      setShowAuthModal(true);
      return;
    }

    try {
      await getProductQty({ user_id: userId, productid: productId, qty: newQty });
      setCartQty(productId, newQty);
    } catch (error) {
      console.error("API Error:", error?.message || error);
      showSnackbar("error", "Failed to update quantity");
    }
  };

  // ----- Wishlist -----
  const AddToWishListHandler = async (productid) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        setPendingCartId(productid);
        setShowAuthModal(true);
        return;
      }

      if (wishlistItems.includes(productid)) return;

      await AddItemToWishList({ customerid: userId, productid });
      showSnackbar("success", "Item Added to Wishlist!");
      setWishlistItems((prev) => [...prev, productid]);
    } catch (error) {
      console.error("Wishlist error:", error);
    }
  };

  const handleDeleteWishList = async (productid) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      await DeleteFromWishList({ customerid: userId, productid });
      setWishlist((prev) => ({ ...prev, [productid]: false }));
    } catch (err) {
      console.error("Error deleting from wishlist:", err);
    }
  };

  const handleNotifyMe = async (productid) => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      setPendingCartId(productid);
      setShowAuthModal(true);
      return;
    }
    try {
      await Notifyed({ customerid: userId, productid });
      showSnackbar("success", "We will notify you when product is available");
    } catch (err) {
      console.error(err);
      showSnackbar("error", "Failed to register notify request");
    }
  };

  // ----- Toggle Models -----
  const toggleModel = (model) => {
    setSelectedModels((prev) =>
      prev.includes(model) ? prev.filter((m) => m !== model) : [...prev, model],
    );
  };
   const getAverageRating = (product) => {
    const ratings = product?.rating;
    if (!ratings || !Array.isArray(ratings) || ratings.length === 0) return null;
    const totalWeight = ratings.reduce((sum, r) => sum + (r.rating || 0) * (r.zcount || 0), 0);
    const totalCount = ratings.reduce((sum, r) => sum + (r.zcount || 0), 0);
    return totalCount > 0 ? (totalWeight / totalCount).toFixed(1) : null;
  };

  return (
    <div className="shop-container search-results-layout">
      {/* ===== Filters ===== */}
      <div className="filters">
        <h3>Product Categories</h3>
        <ul className="category-list">
          {categories.map((c, i) => (
            <li
              key={i}
              className={selectedCategory === c.name ? "active" : ""}
              onClick={() => setSelectedCategory(c.name)}
            >
              <span>{c.name}</span>
              <span className="category-count">({c.count})</span>
            </li>
          ))}
        </ul>

        <div className="price-filter">
          <h4>Filter By Price</h4>
          <div className="price-range">
            <span>Up to ₹{displayMaxPrice.toFixed(2)}</span>
          </div>
          <input
            type="range"
            className="price-slider"
            min="0"
            max={priceLimit}
            step="10"
            value={maxPrice}
            onChange={(e) => setMaxPrice(Number(e.target.value))}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {uniqueBrands?.map((brand) => (
            <label key={brand}>
              <input
                style={{ marginRight: 10 }}
                type="checkbox"
                checked={selectedBrands.includes(brand)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedBrands((prev) => [...prev, brand]);
                  } else {
                    setSelectedBrands((prev) =>
                      prev.filter((b) => b !== brand),
                    );
                  }
                }}
              />
              {brand}
            </label>
          ))}
        </div>
        <div className="discount-filter">
          <h4>Filter By Discount</h4>

          <div className="discount-range">
            <span>{minDiscount}% and above</span>
          </div>

          <input
            type="range"
            className="discount-slider"
            min="0"
            max="80"
            step="5"
            value={minDiscount}
            onChange={(e) => setMinDiscount(Number(e.target.value))}
          />
        </div>

        <button
          className="clear-filters"
          onClick={() => {
            setSelectedCategory(null);
            setSelectedModels([]);
            setMaxPrice(priceLimit);
            setSelectedBrands([]);
          }}
        >
          Clear All Filters
        </button>
      </div>

      {/* ===== Products ===== */}
      <div className="product-content">
        <div className="results-header">
          Showing {filteredProducts.length > 0 ? 1 : 0}-
          {filteredProducts.length} of {filteredProducts.length} items
          {maxPrice < priceLimit && (
            <span className="price-filter-info">
              (Filtered by price: up to ₹{maxPrice.toFixed(2)})
            </span>
          )}
        </div>

        <div className="category-products-home-grid">
          {loading ? (
            <LoadingSpinner text="Loading products..." />
          ) : (
            filteredProducts.map((product, index) => {
              const pricing = getProductPricing(product);
              const discount = Number(product?.discount || product?.dtl?.[0]?.discount || 0);
              const qty =
                itemList.find(
                  (item) => String(item.productId) === String(product.productid),
                )?.count || 0;
              const stock = product.stock || product.dtl?.[0]?.stock || 0;
              const netrate = Number(product.netrate || product.dtl?.[0]?.netrate || 0);
              const savedPurchaseData = getSavedCartPurchaseData(product.productid);
              const purchaseType = savedPurchaseData?.purchaseType || "piece";

              return (
                <div
                  className="category-products-home-card"
                  key={`${product.productid}-${index}`}
                >
                  <button
                    type="button"
                    className="category-products-home-image-btn"
                    onClick={() => goToProductDetail(product.productid)}
                    style={{
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      width: "100%",
                      cursor: "pointer",
                      position: "relative",
                    }}
                  >
                    <div
                      className="bestsellers-actions"
                      style={{
                        position: "absolute",
                        top: "-12px",
                        left: "10px",
                        right: "10px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        zIndex: 2,
                      }}
                    >
                      {isLoggedIn && ((stock > 0 && netrate > 0) || discount > 0) && (
                        <span
                          className="discount-badge"
                          style={{
                            left: "-11px",
                            background: "linear-gradient(135deg, #ff4d4d, #d90429)",
                            color: "#fff",
                            padding: "4px 2px",
                            borderRadius: "6px",
                            fontSize: "11px",
                            fontWeight: "700",
                            lineHeight: 1,
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {stock > 0 && netrate > 0 ? "Netrate" : `${discount}% OFF`}
                        </span>
                      )}

                      <button
                        className="wishlist-btn"
                        style={{
                          position: "absolute",
                          top: "6px",
                          right: "-6px",
                          width: "40px",
                          height: "40px",
                          border: "none",
                          borderRadius: "50%",
                          background: "#fff",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          zIndex: 10,
                        }}
                        onClick={async (e) => {
                          e.stopPropagation();
                          const isInWishlist = wishlistItems.includes(product.productid);

                          if (isInWishlist) {
                            await handleDeleteWishList(product.productid);
                            setWishlistItems((prev) =>
                              prev.filter((id) => id !== product.productid),
                            );
                          } else {
                            await AddToWishListHandler(product.productid);
                          }
                        }}
                        aria-label={
                          wishlistItems.includes(product.productid)
                            ? "Remove from wishlist"
                            : "Add to wishlist"
                        }
                        type="button"
                      >
                        {wishlistItems.includes(product.productid) ? (
                          <FaHeart color="#ff4d4f" size={18} />
                        ) : (
                          <FaRegHeart size={18} />
                        )}
                      </button>
                    </div>
                    <img
                      src={getProductImage(product)}
                      alt={product.modelno || product.displaymodelno || "Product"}
                      onError={(e) => {
                        e.target.src = noimage;
                      }}
                      style={{
                        width: "78%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                        display: "block",
                      }}
                    />
                  </button>

                  <div className="card-brand-row">
                    <span>{product.brand || selectedCategory}</span>

                    {getAverageRating(product) && (
                      <span style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                        <FaStar color="gold" size={18} />
                        <span style={{ fontSize: "15px", fontWeight: "700", color: "#555", paddingTop: '9px' }}>
                          {getAverageRating(product)}
                        </span>
                      </span>
                    )}
                  </div>
                  <strong>
                    {(product.modelno || product.displaymodelno || "")?.toUpperCase()}
                  </strong>

                  <small>
                    {(product.subcategory || product.category || "")
                      ?.charAt(0)
                      .toUpperCase() +
                      (product.subcategory || product.category || "")
                        ?.slice(1)
                        .toLowerCase()}
                  </small>
                  {isLoggedIn && <small>Min order Qty : {product.minorderqty}</small>}

<p className="category-products-home-price-row">
  {Number(product?.mrp || 0) <= 0 ? (
    <span
      className="category-products-home-price"
      style={{
        color: "#005487",
        fontSize: "15px",
        fontWeight: "600",
      }}
    >
     MRP : Coming Soon
    </span>
  ) : (
    <>
      {!isLoggedIn && (
        <span className="category-products-home-mrp" style={{ textDecoration: "none" }}>
          MRP ₹{Number(product.mrp).toFixed(2)}
        </span>
      )}

      {isLoggedIn && Number(
        product?.netrate ||
        product?.dtl?.[0]?.netrate ||
        0
      ) <= 0 && (
        <span className="category-products-home-mrp">
          MRP ₹{Number(product.mrp).toFixed(2)}
        </span>
      )}

      {isLoggedIn && (
        <span
          className="category-products-home-price"
          style={{
            color: "#005487",
            fontSize: "15px",
            fontWeight: "600",
          }}
        >
          Rs.{" "}
          {Number(
            pricing?.netrate > 0
              ? pricing.netrate
              : pricing?.finalPrice || 0
          ).toFixed(2)}
        </span>
      )}
    </>
  )}
</p>
                  {stock <= 0 ? (
                    <button
                      style={{
                        width: "100%",
                        padding: "7px",
                        borderRadius: "25px",
                        border: "none",
                        fontWeight: "600",
                        backgroundColor: "#e0e0e0",
                        color: "#777777",
                        transition: "background-color 0.3s ease",
                        cursor: "pointer",
                      }}
                      onClick={() => handleNotifyMe(product.productid)}
                    >
                      Notify Me
                    </button>
                  ) : qty > 0 ? (
                    <CartQuantityControl
                      qty={qty}
                      minQty={getMinimumPurchaseQuantity(product, purchaseType)}
                      step={purchaseType !== "piece" ? getPurchaseQuantityStep(product, purchaseType) : undefined}
                      stock={stock}
                      purchaseType={purchaseType}
                      product={product}
                      onChange={(newQty) =>
                        handleCartQtyChange(product.productid, newQty)
                      }
                      className="qty-box"
                    />
                  ) : (
                    <button
                      style={{
                        width: "100%",
                        padding: "7px",
                        borderRadius: "25px",
                        border: "none",
                        fontWeight: "600",
                        backgroundColor: "#005487",
                        color: "#ffffff",
                        transition: "background-color 0.3s ease",
                        cursor: "pointer",
                      }}
                      onClick={() => openPurchaseModal(product)}
                    >
                      Add to Cart
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {authModal}

      <PurchaseOptionsModal
        isOpen={showPurchaseModal}
        product={pendingPurchaseProduct}
        onClose={() => {
          setShowPurchaseModal(false);
          setPendingPurchaseProduct(null);
        }}
        onConfirm={confirmPurchaseType}
      />
    </div>
  );
}

export default SearchResults;
